"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _utils = require("../utils");
var _TextInput = require("../TextInput");
var _StackView = _interopRequireDefault(require("../StackView"));
var _InputSupports = _interopRequireDefault(require("../InputSupports"));
var _ThemeProvider = require("../ThemeProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.inputSupportsProps]);
const selectCodeTextInputTokens = _ref => {
  let {
    outerBorderColor,
    outerBackgroundColor
  } = _ref;
  return {
    outerBorderColor,
    outerBackgroundColor,
    icon: null
  };
};
const Validator = /*#__PURE__*/_react.default.forwardRef((_ref2, ref) => {
  let {
    value = '',
    inactive,
    mask = '',
    onChange,
    tokens = {},
    variant = {},
    ...rest
  } = _ref2;
  const defaultRef = _react.default.useRef();
  const codeRef = ref ?? defaultRef;
  const {
    supportsProps
  } = selectProps(rest);
  const strValidation = supportsProps.validation;
  const [, setIndividualCodes] = _react.default.useState({});
  const [text, setText] = _react.default.useState(value);
  const validatorsLength = 6;
  const prefix = 'code';
  const sufixValidation = 'Validation';
  const [isHover, setIsHover] = _react.default.useState(false);
  const handleMouseOver = () => {
    setIsHover(true);
  };
  const handleMouseOut = () => {
    setIsHover(false);
  };
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('TextInput', tokens, variant, {
    hover: isHover
  });
  const [codeReferences, singleCodes] = _react.default.useMemo(() => {
    const codes = [];
    const valueCodes = {};
    Array.from({
      length: validatorsLength
    }, (_, i) => {
      codes[prefix + i] = /*#__PURE__*/_react.default.createRef();
      valueCodes[prefix + i] = '';
      valueCodes[prefix + i + sufixValidation] = '';
      return null;
    });
    return [codes, valueCodes];
  }, [validatorsLength, prefix, sufixValidation]);
  const handleSingleCodes = _react.default.useCallback((codeId, val, validation) => {
    singleCodes[codeId] = val;
    singleCodes[codeId + sufixValidation] = validation;
    setIndividualCodes(prev => ({
      ...prev,
      [codeId]: val
    }));
  }, [singleCodes, sufixValidation]);
  const changeDataMasking = _react.default.useCallback(boxElement => {
    let charMasking = '';
    const element = boxElement;
    if (mask && mask.length === 1) {
      charMasking = mask;
    } else if (mask && mask.length > 1) {
      charMasking = mask.substring(0, 1);
    }
    if (charMasking && element) {
      element.value = charMasking;
    }
  }, [mask]);
  const handleChangeCode = _react.default.useCallback(() => {
    const code = Array.from({
      length: validatorsLength
    }, (_, i) => singleCodes[prefix + i] || '').join('');
    if (typeof onChange === 'function') {
      onChange(code, singleCodes);
    }
  }, [validatorsLength, singleCodes, prefix, onChange]);
  const handleChangeCodeValues = _react.default.useCallback((event, codeId, nextIndex) => {
    const codeElement = codeReferences[codeId]?.current;
    const val = event.nativeEvent?.value || event.target?.value;

    // Only allow numeric characters and limit to single digit
    const numericOnly = val.replace(/\D/g, '').substring(0, 1);
    if (codeElement && codeElement.value) {
      codeElement.value = numericOnly;
    }
    handleSingleCodes(codeId, numericOnly, numericOnly ? 'success' : '');
    handleChangeCode();
    if (nextIndex === validatorsLength) {
      codeElement?.blur();
      changeDataMasking(codeElement);
      return;
    }
    if (numericOnly.length > 0) {
      const nextElement = codeReferences[prefix + nextIndex]?.current;
      nextElement?.focus();
      changeDataMasking(codeElement);
    }
  }, [codeReferences, handleSingleCodes, handleChangeCode, validatorsLength, changeDataMasking, prefix]);
  const handleKeyPress = (event, currentIndex, previousIndex) => {
    if (!(event.keyCode === 8 || event.code === 'Backspace')) {
      return;
    }
    if (currentIndex > 0) {
      const currentElement = codeReferences[prefix + currentIndex]?.current;
      const previousElement = codeReferences[prefix + previousIndex]?.current;
      if (currentElement && currentElement.value) {
        currentElement.value = '';
      }
      previousElement?.focus();
    }
    handleSingleCodes(prefix + currentIndex, '', '');
    handleChangeCode();
  };
  const getCodeComponents = () => {
    return Array.from({
      length: validatorsLength
    }, (_, i) => {
      const codeId = prefix + i;
      const codeInputProps = {
        nativeID: codeId,
        keyboardType: 'numeric',
        ref: codeReferences[codeId] ?? null,
        validation: strValidation || singleCodes[codeId + sufixValidation],
        tokens: selectCodeTextInputTokens(themeTokens),
        onFocus: () => {
          const element = codeReferences[codeId]?.current;
          if (_Platform.default.OS === 'web' && element?.select) {
            return element.select() ?? null;
          }
          if (element?.focus) {
            return element.focus() ?? null;
          }
          return null;
        },
        onKeyPress: event => handleKeyPress(event, i, i - 1),
        onMouseOver: handleMouseOver,
        onMouseOut: handleMouseOut,
        inactive
      };
      if (!codeInputProps.validation) {
        delete codeInputProps.validation;
      }
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: staticStyles.codeInputWidth,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_TextInput.TextInput, {
          ...codeInputProps
        })
      }, codeId);
    });
  };
  _react.default.useEffect(() => {
    if (Number(value).toString() !== 'NaN') {
      setText(value);
    }
  }, [value]);
  _react.default.useEffect(() => {
    Array.from({
      length: validatorsLength
    }, (_, i) => {
      const element = codeReferences[prefix + i]?.current;
      if (element && element.value !== undefined) {
        if (mask && text[i]) {
          element.value = mask;
        } else {
          element.value = text[i] ?? '';
        }
      }
      handleSingleCodes(prefix + i, text[i] ?? '', text[i] ? 'success' : '');
      return null;
    });
  }, [text, mask, validatorsLength, prefix, codeReferences, handleSingleCodes]);
  _react.default.useEffect(() => {
    const handlePasteCode = event => {
      event.preventDefault();

      // Clear current state first
      setText('');

      // Clear all individual input fields and their state
      Array.from({
        length: validatorsLength
      }, (_, i) => {
        const element = codeReferences[prefix + i]?.current;
        if (element && element.value !== undefined) {
          element.value = '';
        }
        handleSingleCodes(prefix + i, '', '');
        return null;
      });
      const clipBoardText = event.clipboardData.getData('text');

      // Validate that input contains only digits and truncate to 6 characters
      const numericOnly = clipBoardText.replace(/\D/g, '').substring(0, validatorsLength);
      if (numericOnly.length > 0) {
        setText(numericOnly);
      }
    };
    const handleCopy = event => {
      const clipBoardText = Array.from({
        length: validatorsLength
      }, (_, i) => singleCodes[prefix + i] || '').join('');
      event.clipboardData.setData('text/plain', clipBoardText);
      event.preventDefault();
    };
    if (_Platform.default.OS === 'web') {
      Array.from({
        length: validatorsLength
      }, (_, i) => {
        const element = codeReferences[prefix + i]?.current;
        if (element && typeof element.addEventListener === 'function') {
          element.addEventListener('paste', handlePasteCode);
          element.addEventListener('copy', handleCopy);
          element.addEventListener('input', event => handleChangeCodeValues(event, prefix + i, i + 1));
        }
        return null;
      });
    }
    return () => {
      if (_Platform.default.OS === 'web') {
        Array.from({
          length: validatorsLength
        }, (_, i) => {
          const element = codeReferences[prefix + i]?.current;
          if (element && typeof element.removeEventListener === 'function') {
            element.removeEventListener('paste', handlePasteCode);
            element.removeEventListener('copy', handleCopy);
            element.removeEventListener('input', event => handleChangeCodeValues(event, prefix + i, i + 1));
          }
          return null;
        });
      }
    };
  }, [validatorsLength, prefix, codeReferences, handleChangeCodeValues, handleSingleCodes, singleCodes]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_InputSupports.default, {
    ...supportsProps,
    feedbackProps: {
      ...rest?.feedbackProps,
      showFeedbackIcon: true
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_StackView.default, {
      space: 2,
      direction: "row",
      ref: codeRef,
      children: getCodeComponents()
    })
  });
});
Validator.displayName = 'Validator';
Validator.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * The value is a 6-digit code, may be only numeric characters, non numeric character aren't renderize
   */
  value: _propTypes.default.string,
  /**
   * If true, the component is inactive and non editable.
   */
  inactive: _propTypes.default.bool,
  /**
   * Show masked characters and obscure the actual input. For example: '*'
   */
  mask: _propTypes.default.string,
  /**
   * Use to react upon input's value changes. Required when the `value` prop is set. Will receive the input's value as an argument.
   */
  onChange: _propTypes.default.func
};
var _default = exports.default = Validator;
const staticStyles = _StyleSheet.default.create({
  codeInputWidth: {
    width: 43
  }
});